/**
 * Mock personal records service.
 * Mirrors personal-records.service.js API with in-memory data.
 */
import { MOCK_PERSONAL_RECORDS } from '../../data/mock/mock-personal-records.data';
import { mockDelay, mockLog } from './mock-delay';

// In-memory state
let records = [...MOCK_PERSONAL_RECORDS];

/**
 * Check all exercises in a completed workout for new PRs.
 * Returns array of new PR objects for celebration display.
 */
export const checkAndUpdatePRs = async (userId, workout) => {
  mockLog('PersonalRecordsService', 'checkAndUpdatePRs', workout.title);
  await mockDelay();

  const newPRs = [];
  const exercises = workout.exercises || [];

  for (const exercise of exercises) {
    const exerciseName = exercise.name;
    if (!exerciseName) continue;

    const nameKey = exerciseName.toLowerCase();
    const existing = records.find(
      (r) => r.exerciseName.toLowerCase() === nameKey && r.userId === userId
    );

    let updated = false;

    for (const set of exercise.sets || []) {
      const weight = parseFloat(set.weight) || 0;
      const reps = parseInt(set.reps, 10) || 0;
      const volume = weight * reps;
      const unit = set.unit || 'lbs';

      // Check max weight PR
      if (weight > 0 && (!existing?.maxWeight || weight > existing.maxWeight.value)) {
        newPRs.push({ exerciseName, type: 'weight', value: weight, unit });
        updated = true;
      }

      // Check max volume PR
      if (volume > 0 && (!existing?.maxVolume || volume > existing.maxVolume.value)) {
        if (!newPRs.find((p) => p.exerciseName === exerciseName && p.type === 'volume')) {
          newPRs.push({ exerciseName, type: 'volume', value: volume, unit });
        }
        updated = true;
      }

      // Check max reps PR
      if (reps > 0 && (!existing?.maxReps || reps > existing.maxReps.value)) {
        if (!newPRs.find((p) => p.exerciseName === exerciseName && p.type === 'reps')) {
          newPRs.push({ exerciseName, type: 'reps', value: reps, unit });
        }
        updated = true;
      }
    }

    // Update in-memory records if PR found
    if (updated && existing) {
      const idx = records.indexOf(existing);
      records[idx] = { ...existing, updatedAt: new Date().toISOString() };
    } else if (updated && !existing) {
      records.push({
        exerciseName,
        userId,
        maxWeight: null,
        maxVolume: null,
        maxReps: null,
        history: [],
        updatedAt: new Date().toISOString(),
      });
    }
  }

  return newPRs;
};

/**
 * Get all PRs for a user.
 */
export const getPRs = async (userId) => {
  mockLog('PersonalRecordsService', 'getPRs', userId);
  await mockDelay();
  return records.filter((r) => r.userId === userId);
};

/**
 * Get PR for a specific exercise.
 */
export const getPRForExercise = async (userId, exerciseName) => {
  mockLog('PersonalRecordsService', 'getPRForExercise', exerciseName);
  await mockDelay();
  const nameKey = exerciseName.toLowerCase();
  return records.find(
    (r) => r.exerciseName.toLowerCase() === nameKey && r.userId === userId
  ) || null;
};

/**
 * Get most recent PRs across all exercises.
 */
export const getRecentPRs = async (userId, max = 10) => {
  mockLog('PersonalRecordsService', 'getRecentPRs', { userId, max });
  await mockDelay();

  const userRecords = records.filter((r) => r.userId === userId);
  const recentEntries = [];

  userRecords.forEach((pr) => {
    (pr.history || []).forEach((h) => {
      recentEntries.push({
        exerciseName: pr.exerciseName,
        type: h.type,
        value: h.value,
        date: h.date,
        maxWeight: pr.maxWeight,
        maxVolume: pr.maxVolume,
        maxReps: pr.maxReps,
      });
    });
  });

  recentEntries.sort((a, b) => new Date(b.date) - new Date(a.date));
  return recentEntries.slice(0, max);
};
